/**
 * Application Constants Configuration
 *
 * Centralized configuration for all magic numbers, timeouts, limits, and other
 * configurable values used throughout the application.
 *
 * Benefits:
 * - Single source of truth for all configuration values
 * - Easy to maintain and update
 * - Consistent behavior across the application
 * - Easy to test different configurations
 */

/**
 * RTK Query Cache Configuration
 * Controls how long cached data is retained before being garbage collected
 * Values are in seconds
 */
export const CACHE_TIME = {
  SHORT: 30, // 30 seconds - frequently changing data (messages, real-time data)
  MEDIUM: 60, // 60 seconds - moderate change frequency (analytics, payments)
  LONG: 600, // 10 minutes - rarely changing data (stores, events)
  VERY_LONG: 1800, // 30 minutes - static or rarely updated data (malls, offer details)
} as const;

/**
 * Debounce Delays (in milliseconds)
 * Controls the delay before triggering actions after user stops typing/interacting
 */
export const DEBOUNCE_DELAYS = {
  SEARCH: 1000, // 1 second - Main search inputs (Explore page search bar)
  MALL_SELECTOR: 500, // 500ms - Mall selector search (faster response needed)
  STORE_SEARCH: 1000, // 1 second - Store search in MallHub
  DEFAULT: 300, // 300ms - Default debounce for Redux hooks and general use
} as const;

/**
 * Pagination Configuration
 * Controls the number of items displayed per page/request
 */
export const PAGINATION = {
  // Explore page limits
  MALL_PREVIEW_LIMIT: 4, // Number of malls to show in preview/initial load
  MALL_SELECTOR_LIMIT: 20, // Number of malls in dropdown selector
  OFFERS_PREVIEW_LIMIT: 4, // Number of offers to show in preview
  EVENTS_PREVIEW_LIMIT: 4, // Number of events to show in preview

  // Page limits for individual screens (Offers, Events, Stores)
  PAGE_LIMIT: 4, // Number of items per page for infinite scroll in detail screens

  // Infinite scroll defaults
  DEFAULT_PAGE_SIZE: 12, // Default items per page for infinite scroll

  // Detail page limits
  RELATED_ITEMS_LIMIT: 8, // Number of related items to show (similar offers/events)

  // Table pagination
  TABLE_ROWS_PER_PAGE: [10, 25, 50, 100], // Options for table pagination
  TABLE_DEFAULT_ROWS: 25, // Default rows per page in tables
} as const;

/**
 * Search Configuration
 * Controls search behavior and validation
 */
export const SEARCH = {
  MIN_SEARCH_LENGTH: 2, // Minimum characters required to trigger search
  MAX_SEARCH_LENGTH: 100, // Maximum search query length
  MIN_MALL_NAME_LENGTH: 2, // Minimum characters for mall name search
  MIN_STORE_NAME_LENGTH: 2, // Minimum characters for store name search
} as const;

/**
 * Animation Configuration
 * Controls animation timings and delays for framer-motion
 */
export const ANIMATION = {
  // Duration values (in seconds)
  DURATION: {
    FAST: 0.2, // Fast animations (hover effects, simple transitions)
    NORMAL: 0.4, // Normal animations (card animations, most UI elements)
    SLOW: 0.6, // Slow animations (page transitions, complex animations)
    VERY_SLOW: 0.8, // Very slow (hero sections, major transitions)
  },

  // Stagger delays for list animations (in seconds)
  STAGGER: {
    FAST: 0.05, // Fast stagger between items
    NORMAL: 0.1, // Normal stagger
    SLOW: 0.15, // Slow stagger
  },

  // Initial delays for staged animations
  DELAY: {
    NONE: 0,
    SHORT: 0.2,
    MEDIUM: 0.3,
    LONG: 0.5,
  },
} as const;

/**
 * UI/UX Configuration
 * Controls various UI behaviors and thresholds
 */
export const UI = {
  // Loading states
  MIN_LOADING_TIME: 300, // Minimum time to show loading state (prevents flashing)
  SKELETON_ANIMATION_DURATION: 1.5, // Duration of skeleton loading animation

  // Toast/Snackbar durations (milliseconds)
  TOAST_DURATION: {
    SHORT: 2000, // 2 seconds
    NORMAL: 4000, // 4 seconds (default)
    LONG: 6000, // 6 seconds
  },

  // Scroll behavior
  SMOOTH_SCROLL_DURATION: 500, // Duration for smooth scroll animations
  INFINITE_SCROLL_THRESHOLD: 200, // Pixels from bottom to trigger load more

  // Hover delays
  TOOLTIP_DELAY: 500, // Delay before showing tooltips

  // Image loading
  IMAGE_LAZY_LOAD_THRESHOLD: 100, // Pixels before viewport to start loading images
} as const;

/**
 * API Configuration
 * Controls API request behavior and retries
 */
export const API = {
  // Retry configuration
  MAX_RETRIES: 3, // Maximum number of retry attempts
  RETRY_DELAY: 1000, // Base delay between retries (exponential backoff)

  // Timeout configuration
  REQUEST_TIMEOUT: 30000, // 30 seconds - Maximum time for API request

  // Polling intervals (milliseconds)
  POLL_INTERVAL: {
    FAST: 5000, // 5 seconds - Real-time data
    NORMAL: 30000, // 30 seconds - Semi-real-time
    SLOW: 60000, // 1 minute - Background updates
  },
} as const;

/**
 * Form Validation Configuration
 * Controls form validation behavior
 */
export const VALIDATION = {
  // Email validation
  EMAIL_MAX_LENGTH: 255,

  // Password validation
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,

  // Text field validation
  TEXT_MIN_LENGTH: 1,
  TEXT_MAX_LENGTH: 500,

  // Textarea validation
  TEXTAREA_MAX_LENGTH: 2000,

  // Phone number
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 15,
} as const;

/**
 * Feature Flags
 * Controls feature availability across the application
 */
export const FEATURES = {
  ENABLE_ANALYTICS: true,
  ENABLE_DEBUG_LOGS: process.env.NODE_ENV === "development",
  ENABLE_PERFORMANCE_MONITORING: true,
  ENABLE_ERROR_REPORTING: process.env.NODE_ENV === "production",
} as const;

/**
 * Storage Keys
 * Keys used for localStorage/sessionStorage
 * Centralized to prevent typos and ensure consistency
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  USER_DATA: "user_data",
  THEME_PREFERENCE: "theme_preference",
  LANGUAGE_PREFERENCE: "language_preference",
  RECENT_SEARCHES: "recent_searches",
  VIEWED_OFFERS: "viewed_offers",
} as const;

/**
 * Route Paths
 * Centralized route definitions for easy maintenance
 */
export const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  EXPLORE: "/explore",
  MALL_HUB: "/mall/:mallId",
  STORE_DETAILS: "/store/:storeId",
  OFFER_DETAILS: "/offer/:offerId",
  EVENT_DETAILS: "/event/:eventId",
  DASHBOARD: "/dashboard",
  ANALYTICS: "/analytics",
  MESSAGES: "/messages",
  PAYMENTS: "/payments",
} as const;

/**
 * Export all constants as a single object for convenience
 */
export const APP_CONSTANTS = {
  CACHE_TIME,
  DEBOUNCE_DELAYS,
  PAGINATION,
  SEARCH,
  ANIMATION,
  UI,
  API,
  VALIDATION,
  FEATURES,
  STORAGE_KEYS,
  ROUTES,
} as const;

// Export type for TypeScript inference
export type AppConstants = typeof APP_CONSTANTS;
